<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import UserMultiSelect from "@/components/UserMultiSelect.vue";
import { toDateTimeLocal } from "@/lib/date";
import { fetchUsers } from "@/features/users/api";
import { fetchTaskReminders, fetchTaskGroup, addTaskGroup, editTaskGroup, editReminderAudience } from "./api";

const route = useRoute(), router = useRouter();
const id = computed(() => route.params.id || null);
const isEdit = computed(() => !!id.value);
const uuid = () => crypto.randomUUID ? crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const loading = ref(false), notFound = ref(false), saving = ref(false), success = ref(false), error = ref("");
const taskMain = ref(""), startDate = ref(""), endDate = ref(""), taskReminder = ref(""), selectedUsers = ref([]), tasks = ref([]);
const reminders = ref([]), users = ref([]), usersLoading = ref(false);
const reminderItems = computed(() => reminders.value.map(r => ({ title: r.title, value: r._id })));

onMounted(async () => {
  usersLoading.value = true;
  try {
    const [rem, userList] = await Promise.all([fetchTaskReminders().catch(() => []), fetchUsers().catch(() => [])]);
    reminders.value = Array.isArray(rem) ? rem : [];
    users.value = (Array.isArray(userList) ? userList : []).map(u => ({ ...u, id: u.objectID ?? u._id ?? u.id }));
  } finally { usersLoading.value = false; }
  if (!isEdit.value) return;
  loading.value = true;
  try {
    const g = await fetchTaskGroup(id.value);
    if (!g) { notFound.value = true; return; }
    tasks.value = Array.isArray(g.tasks) ? g.tasks : []; taskMain.value = g.title ?? ""; selectedUsers.value = Array.isArray(g.userGuids) ? g.userGuids : []; taskReminder.value = g.associatedTask ?? ""; startDate.value = toDateTimeLocal(g.startDate); endDate.value = toDateTimeLocal(g.endDate);
  } catch { error.value = "Couldn't load the task."; }
  finally { loading.value = false; }
});

const taskDialog = ref(false), taskDraft = ref({ id: null, title: "", note: "", url: "" });
const subtaskDialog = ref(false), subtaskDraft = ref({ id: null, taskId: null, title: "", note: "", url: "" });

function openAddTask() { taskDraft.value = { id: null, title: "", note: "", url: "" }; taskDialog.value = true; }
function openEditTask(t) { taskDraft.value = { id: t.id, title: t.title, note: t.note, url: t.url }; taskDialog.value = true; }
function saveTask() {
  if (!taskDraft.value.title) return;
  if (taskDraft.value.id) { const t = tasks.value.find(x => x.id === taskDraft.value.id); if (t) { t.title = taskDraft.value.title; t.note = taskDraft.value.note; t.url = taskDraft.value.url; } }
  else tasks.value.push({ id: uuid(), title: taskDraft.value.title, note: taskDraft.value.note, url: taskDraft.value.url, isCompleted: false, subtasks: [] });
  taskDialog.value = false;
}
function openAddSubtask(taskId) { subtaskDraft.value = { id: null, taskId, title: "", note: "", url: "" }; subtaskDialog.value = true; }
function openEditSubtask(taskId, st) { subtaskDraft.value = { id: st.id, taskId, title: st.title, note: st.note, url: st.url }; subtaskDialog.value = true; }
function saveSubtask() {
  if (!subtaskDraft.value.title) return;
  const task = tasks.value.find(t => t.id === subtaskDraft.value.taskId); if (!task) return;
  if (!Array.isArray(task.subtasks)) task.subtasks = [];
  if (subtaskDraft.value.id) { const st = task.subtasks.find(s => s.id === subtaskDraft.value.id); if (st) { st.title = subtaskDraft.value.title; st.note = subtaskDraft.value.note; st.url = subtaskDraft.value.url; } }
  else task.subtasks.push({ id: uuid(), title: subtaskDraft.value.title, note: subtaskDraft.value.note, url: subtaskDraft.value.url, isCompleted: false });
  subtaskDialog.value = false;
}

async function submit() {
  if (!tasks.value.length) { error.value = "Add at least one task."; return; }
  success.value = false; error.value = ""; saving.value = true;
  try {
    const base = { tasks: tasks.value, title: taskMain.value, userGuids: selectedUsers.value, startDate: startDate.value, endDate: endDate.value, associatedTask: taskReminder.value };
    if (isEdit.value) await editTaskGroup({ ...base, docId: id.value }); else await addTaskGroup(base);
    if (taskReminder.value) await editReminderAudience({ userGuids: selectedUsers.value, startDate: startDate.value, endDate: endDate.value, itemId: taskReminder.value });
    success.value = true; setTimeout(() => router.push({ name: "tasks" }), 800);
  } catch { error.value = "Error occurred. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'tasks' })">Back</VBtn>
    <h1 class="text-h5 mb-4">{{ isEdit ? "Edit Task" : "Add Task" }}</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Task not found.</VAlert>
    <VCard v-else class="pa-4" max-width="800">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved. Redirecting…</VAlert>
      <form @submit.prevent="submit">
        <VRow>
          <VCol cols="12"><VTextField v-model="taskMain" label="Main Title" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="startDate" label="Start Date" type="datetime-local" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="endDate" label="End Date" type="datetime-local" /></VCol>
          <VCol cols="12">
            <VAutocomplete v-model="taskReminder" label="Associated Reminder" :items="reminderItems" item-title="title" item-value="value" clearable placeholder="Select…" auto-select-first />
          </VCol>
          <VCol cols="12">
            <UserMultiSelect v-model="selectedUsers" :users="users" :loading="usersLoading" label="Audience" />
          </VCol>
        </VRow>

        <!-- Tasks list -->
        <VCard variant="outlined" class="mt-4 pa-4">
          <div class="d-flex justify-space-between align-center mb-3">
            <div class="text-body-1 font-weight-semibold">Tasks</div>
            <VBtn size="small" variant="tonal" @click="openAddTask">Add Task</VBtn>
          </div>
          <p v-if="!tasks.length" class="text-medium-emphasis">No tasks yet. Add at least one.</p>
          <div v-for="task in tasks" :key="task.id" class="task-item mb-3">
            <div class="d-flex justify-space-between align-start">
              <div>
                <div class="font-weight-medium">{{ task.title }}</div>
                <div v-if="task.note" class="text-caption text-medium-emphasis">{{ task.note }}</div>
                <a v-if="task.url" :href="task.url" target="_blank" class="text-caption text-primary">{{ task.url }}</a>
              </div>
              <div class="d-flex gap-1">
                <VBtn size="x-small" variant="text" @click="openAddSubtask(task.id)">+ Sub</VBtn>
                <VBtn size="x-small" variant="text" @click="openEditTask(task)">Edit</VBtn>
                <VBtn size="x-small" variant="text" color="error" @click="tasks = tasks.filter(t => t.id !== task.id)">Del</VBtn>
              </div>
            </div>
            <ul v-if="task.subtasks?.length" class="pl-4 mt-1">
              <li v-for="st in task.subtasks" :key="st.id" class="d-flex justify-space-between align-start text-body-2 mb-1">
                <span>{{ st.title }}</span>
                <div class="d-flex gap-1">
                  <VBtn size="x-small" variant="text" @click="openEditSubtask(task.id, st)">Edit</VBtn>
                  <VBtn size="x-small" variant="text" color="error" @click="task.subtasks = task.subtasks.filter(s => s.id !== st.id)">Del</VBtn>
                </div>
              </li>
            </ul>
          </div>
        </VCard>

        <div class="d-flex gap-2 mt-4">
          <VBtn type="submit" :loading="saving" :disabled="!tasks.length">Submit</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'tasks' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>

    <!-- Task dialog -->
    <VDialog v-model="taskDialog" max-width="480">
      <VCard :title="taskDraft.id ? 'Edit Task' : 'Add Task'">
        <VCardText>
          <VTextField v-model="taskDraft.title" label="Title" class="mb-3" />
          <VTextField v-model="taskDraft.note" label="Note" class="mb-3" />
          <VTextField v-model="taskDraft.url" label="URL" />
        </VCardText>
        <VCardActions class="justify-end px-4 pb-4 gap-2">
          <VBtn variant="tonal" @click="taskDialog = false">Cancel</VBtn>
          <VBtn :disabled="!taskDraft.title" @click="saveTask">{{ taskDraft.id ? "Save" : "Add" }}</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Subtask dialog -->
    <VDialog v-model="subtaskDialog" max-width="480">
      <VCard :title="subtaskDraft.id ? 'Edit Subtask' : 'Add Subtask'">
        <VCardText>
          <VTextField v-model="subtaskDraft.title" label="Title" class="mb-3" />
          <VTextField v-model="subtaskDraft.note" label="Note" class="mb-3" />
          <VTextField v-model="subtaskDraft.url" label="URL" />
        </VCardText>
        <VCardActions class="justify-end px-4 pb-4 gap-2">
          <VBtn variant="tonal" @click="subtaskDialog = false">Cancel</VBtn>
          <VBtn :disabled="!subtaskDraft.title" @click="saveSubtask">{{ subtaskDraft.id ? "Save" : "Add" }}</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>

<style scoped>
.task-item { padding: 8px; border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity)); border-radius: 6px; }
</style>
